import { ArrowRight, Check, LucideIcon } from 'lucide-react'
import React, { useEffect, useRef, ReactNode } from 'react'
import { BeforeAfterScenario, HeroSystemVisual, LeverageGraph, VisualVariant } from './visual-systems'
import { buttonClassName } from './button'
import { cn } from '../../lib/utils'

interface VerticalMarqueeProps {
  children: ReactNode
  reverse?: boolean
  className?: string
  speed?: number
}

function VerticalMarquee({ children, reverse = false, className, speed = 30 }: VerticalMarqueeProps) {
  return (
    <div
      className={cn('group flex flex-col overflow-hidden', className)}
      style={{ '--duration': `${speed}s` } as React.CSSProperties}
    >
      <div
        className={cn(
          'flex shrink-0 flex-col animate-marquee-vertical',
          reverse && '[animation-direction:reverse]',
        )}
      >
        {children}
      </div>
      <div
        className={cn(
          'flex shrink-0 flex-col animate-marquee-vertical',
          reverse && '[animation-direction:reverse]',
        )}
        aria-hidden="true"
      >
        {children}
      </div>
    </div>
  )
}

export type TrustCard = {
  copy: string
  title: string
}

export type TrustPageProps = {
  beforeCta?: React.ReactNode
  cards: TrustCard[]
  ctaCopy?: string
  ctaMarqueeItems?: string[]
  ctaTitle?: string
  eyebrow: string
  heroCopy: string
  heroTitle: string
  Icon: LucideIcon
  proof: string[]
  sections: Array<{
    copy: string
    items: string[]
    kicker: string
    title: string
  }>
  visual?: VisualVariant
}

const DEFAULT_MARQUEE_ITEMS = [
  'Seed-stage Founders',
  'Series A CTOs',
  'Product-led Teams',
  'Scaling Startups',
  'Technical Co-founders',
]

export function TrustPage({
  beforeCta,
  cards,
  ctaCopy = 'Bring one messy problem to the diagnostic. We will turn it into a map, a priority sequence, and a next move.',
  ctaMarqueeItems = DEFAULT_MARQUEE_ITEMS,
  ctaTitle = 'Ready to diagnose the circuit?',
  eyebrow,
  heroCopy,
  heroTitle,
  Icon,
  proof,
  sections,
  visual = 'cto',
}: TrustPageProps) {
  const marqueeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = marqueeRef.current
    if (!container) return

    const updateOpacity = () => {
      const items = container.querySelectorAll<HTMLElement>('.marquee-item')
      const rect = container.getBoundingClientRect()
      const centerY = rect.top + rect.height / 2
      items.forEach((item) => {
        const itemRect = item.getBoundingClientRect()
        const dist = Math.abs(centerY - (itemRect.top + itemRect.height / 2))
        const opacity = 1 - Math.min(dist / (rect.height / 2), 1) * 0.75
        item.style.opacity = String(opacity)
      })
    }

    let frame: number
    const loop = () => { updateOpacity(); frame = requestAnimationFrame(loop) }
    frame = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(frame)
  }, [])
  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-800">
        <div className="absolute inset-0 circuit-grid opacity-40" aria-hidden="true" />
        <div className="absolute inset-x-0 top-0 h-80 bg-[radial-gradient(circle_at_50%_0%,rgba(56,189,248,0.18),transparent_45%)]" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 sm:px-8 lg:grid-cols-[1fr_0.8fr] lg:py-28">
          <div>
            <p className="section-kicker">{eyebrow}</p>
            <h1 className="mt-5 max-w-4xl text-5xl font-black leading-[0.96] tracking-tight text-white sm:text-6xl">
              {heroTitle}
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-300">{heroCopy}</p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a href="/book" className={buttonClassName({ size: 'lg' })}>
                Book the 18-minute diagnostic
                <ArrowRight className="h-4 w-4" />
              </a>
              <a href="/pricing" className={buttonClassName({ size: 'lg', variant: 'outline' })}>
                View pricing
              </a>
            </div>
          </div>
          <aside>
            <HeroSystemVisual variant={visual} />
          </aside>
        </div>
      </section>

      {/* Proof bar */}
      <section className="border-b border-slate-800 bg-slate-900/40">
        <div className="mx-auto max-w-7xl px-5 py-5 sm:px-8">
          <div className="flex flex-wrap items-start gap-x-6 gap-y-4 sm:items-center">
            <div className="flex flex-none items-center gap-2.5">
              <Icon className="h-5 w-5 flex-none text-sky-300" />
              <span className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-sky-300 whitespace-nowrap">
                Trust signals
              </span>
            </div>
            <div className="hidden h-4 w-px bg-slate-700 sm:block" aria-hidden="true" />
            <ul className="flex flex-wrap gap-x-6 gap-y-2">
              {proof.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm leading-6 text-slate-400 sm:items-center">
                  <Check className="mt-0.5 h-3.5 w-3.5 flex-none text-sky-300 sm:mt-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Cards */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="grid gap-5 lg:grid-cols-3">
          {cards.map((card) => (
            <article key={card.title} className="rounded-[1.5rem] border border-slate-800 bg-slate-900/80 p-6 shadow-[0_18px_60px_rgba(2,6,23,0.28)]">
              <h2 className="text-2xl font-black text-white">{card.title}</h2>
              <p className="mt-4 leading-7 text-slate-300">{card.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-800 bg-slate-900/50">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 py-16 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          <BeforeAfterScenario variant={visual} />
          <LeverageGraph variant={visual} />
        </div>
      </section>

      {sections.map((section, index) => (
        <section
          key={section.title}
          className={`${index % 2 === 0 ? 'border-y border-slate-800 bg-slate-900/60' : ''}`}
        >
          <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 sm:px-8 lg:grid-cols-[0.82fr_1.18fr]">
            <div>
              <p className="section-kicker">{section.kicker}</p>
              <h2 className="section-title">{section.title}</h2>
              <p className="mt-5 leading-7 text-slate-300">{section.copy}</p>
            </div>
            <div className="grid gap-5 lg:grid-cols-[0.95fr_1.05fr]">
              <div className="relative min-h-64 overflow-hidden rounded-[1.5rem] border border-slate-800 bg-slate-950 p-5">
                <div className="absolute inset-0 visual-grid opacity-50" aria-hidden="true" />
                <div className="relative flex h-full flex-col justify-between">
                  <div>
                    <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-sky-300">Scenario map</p>
                    <h3 className="mt-3 text-2xl font-black leading-tight text-white">{section.title}</h3>
                  </div>
                  <div className="mt-8 space-y-3">
                    <div className="h-2 w-3/5 rounded-full bg-slate-700" />
                    <div className="h-2 w-4/5 rounded-full bg-sky-300" />
                    <div className="h-2 w-2/5 rounded-full bg-emerald-300" />
                  </div>
                  <div className="mt-8 grid grid-cols-3 gap-2">
                    {[0, 1, 2].map((node) => (
                      <span key={node} className="h-14 rounded-xl border border-sky-300/25 bg-sky-300/10" />
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid gap-3">
                {section.items.map((item) => (
                  <div key={item} className="flex gap-3 rounded-2xl border border-slate-800 bg-slate-950 p-4 text-slate-300">
                    <Check className="mt-0.5 h-4 w-4 flex-none text-sky-300" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {beforeCta}

      <section className="relative overflow-hidden border-t border-slate-800">
        <div className="absolute inset-0 circuit-grid opacity-20" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-5 py-20 sm:px-8 lg:grid-cols-2 lg:gap-24 lg:items-center lg:py-28">
          {/* Left content */}
          <div className="space-y-8 max-w-xl animate-fade-in-up">
            <p className="section-kicker">Next step</p>
            <h2 className="text-5xl font-black leading-[0.96] tracking-tight text-white sm:text-6xl">
              {ctaTitle}
            </h2>
            <p className="text-lg leading-8 text-slate-300">{ctaCopy}</p>
            <div className="flex flex-wrap gap-4">
              <a
                href="/book"
                className={buttonClassName({ size: 'lg' })}
              >
                Book diagnostic
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="/pricing"
                className={buttonClassName({ size: 'lg', variant: 'outline' })}
              >
                View pricing
              </a>
            </div>
          </div>

          {/* Right marquee */}
          <div
            ref={marqueeRef}
            className="relative h-[480px] lg:h-[560px] flex items-center justify-center animate-fade-in-up [animation-delay:300ms]"
          >
            <div className="relative w-full h-full">
              <VerticalMarquee speed={22} className="h-full">
                {ctaMarqueeItems.map((item, idx) => (
                  <div
                    key={idx}
                    className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight py-7 text-white marquee-item"
                  >
                    {item}
                  </div>
                ))}
              </VerticalMarquee>

              {/* Top fade */}
              <div className="pointer-events-none absolute top-0 inset-x-0 h-48 bg-gradient-to-b from-slate-950 via-slate-950/60 to-transparent z-10" />
              {/* Bottom fade */}
              <div className="pointer-events-none absolute bottom-0 inset-x-0 h-48 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent z-10" />
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}
